import { createContext, type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

/**
 * Inside the side panel a form opens on a supplier (U1), a link must not leave the unsaved form: a
 * supplier opens in the same panel, an expense in a new tab. On the supplier's own page there is no
 * panel, and the links navigate as usual.
 */
type PanelLinks = { readonly openSupplier: (supplierId: string) => void } | null;

const PanelLinksContext = createContext<PanelLinks>(null);

export function PanelLinksProvider({
  openSupplier,
  children,
}: {
  readonly openSupplier: (supplierId: string) => void;
  readonly children: ReactNode;
}) {
  return (
    <PanelLinksContext.Provider value={{ openSupplier }}>{children}</PanelLinksContext.Provider>
  );
}

/** A link to a supplier: its page, or the same panel when shown beside a form. */
export function SupplierLink({
  supplierId,
  className,
  children,
}: {
  readonly supplierId: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const panel = useContext(PanelLinksContext);
  return (
    <Link
      to={`/expenses/suppliers/${encodeURIComponent(supplierId)}`}
      className={className}
      {...(panel
        ? {
            onClick: (event: React.MouseEvent) => {
              // A modified click (new tab, new window) keeps the browser's own way.
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              panel.openSupplier(supplierId);
            },
          }
        : {})}
    >
      {children}
    </Link>
  );
}

/** A link to an expense: its page, or a new tab when shown beside a form. */
export function ExpenseLink({
  expenseId,
  className,
  children,
}: {
  readonly expenseId: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation('suppliers');
  const panel = useContext(PanelLinksContext);
  return (
    <Link
      to={`/expenses/${encodeURIComponent(expenseId)}`}
      className={className}
      {...(panel ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {children}
      {panel ? <span className="sr-only">{` ${t('panel.newTab')}`}</span> : null}
    </Link>
  );
}
